"use client";

import React from "react";
import { PageContainer } from "@/src/components/layout/page-container";
import { Section } from "@/src/components/layout/section";
import { SectionCard } from "@/src/components/container/section-card";
import { CardTitle } from "@/src/components/container/card";
import { Text } from "@/src/components/information/text";
import { Badge } from "@/src/components/information/badge";

export default function AIInstructionsPage() {
  return (
    <PageContainer
      size="xl"
      className="py-theme-6 px-theme-6 space-y-theme-5 overflow-auto h-full"
    >
      {/* Header */}
      <div className="space-y-theme-2">
        <Text as="h2" className="text-h2 font-bold tracking-tight">
          BlueCopa UI AI Instructions
        </Text>
        <Text as="p" className="text-body text-muted-foreground">
          Essential patterns and rules for generating production-ready code with
          BlueCopa components
        </Text>
      </div>

      {/* Core Principles */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Core Principles</CardTitle>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <Badge variant="default" className="mt-1">
                  1
                </Badge>
                <div>
                  <Text className="font-semibold">
                    Always follow the layout hierarchy
                  </Text>
                  <Text className="text-sm text-muted-foreground">
                    Never skip levels — Scaffold → PageContainer → Section →
                    SectionCard
                  </Text>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge variant="default" className="mt-1">
                  2
                </Badge>
                <div>
                  <Text className="font-semibold">
                    Always use the controller pattern
                  </Text>
                  <Text className="text-sm text-muted-foreground">
                    Separate business logic from UI views
                  </Text>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge variant="default" className="mt-1">
                  3
                </Badge>
                <div>
                  <Text className="font-semibold">
                    Always use registry imports
                  </Text>
                  <Text className="text-sm text-muted-foreground">
                    Components from @/components/ui/bluecopa/ui/
                  </Text>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge variant="default" className="mt-1">
                  4
                </Badge>
                <div>
                  <Text className="font-semibold">
                    Never hardcode styling
                  </Text>
                  <Text className="text-sm text-muted-foreground">
                    Use theme tokens and utility classes
                  </Text>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge variant="default" className="mt-1">
                  5
                </Badge>
                <div>
                  <Text className="font-semibold">
                    Never mix chart libraries
                  </Text>
                  <Text className="text-sm text-muted-foreground">
                    ECharts only, wrapped in ChartProvider
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Component Cheat Sheet */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Component Quick Reference</CardTitle>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2 text-sm">Tables:</Text>
                <div className="bg-muted p-3 rounded-md text-sm">
                  <div className="flex justify-between mb-1">
                    <span>&lt; 10K rows</span>
                    <span className="font-mono text-xs">DataTable</span>
                  </div>
                  <div className="flex justify-between">
                    <span>&gt; 10K rows</span>
                    <span className="font-mono text-xs">AGGridTable</span>
                  </div>
                </div>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">Loading:</Text>
                <div className="bg-muted p-3 rounded-md text-sm">
                  <div className="flex justify-between mb-1">
                    <span>Layout loading</span>
                    <span className="font-mono text-xs">Skeleton</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Action loading</span>
                    <span className="font-mono text-xs">Spinner</span>
                  </div>
                </div>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">Buttons:</Text>
                <div className="bg-muted p-3 rounded-md text-sm space-y-1">
                  <div>
                    <span className="font-mono text-xs">variant="solid"</span>
                    — Primary action
                  </div>
                  <div>
                    <span className="font-mono text-xs">variant="outline"</span>
                    — Secondary
                  </div>
                  <div>
                    <span className="font-mono text-xs">variant="ghost"</span>—
                    Tertiary
                  </div>
                </div>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">Status:</Text>
                <div className="bg-muted p-3 rounded-md text-sm space-y-1">
                  <div>
                    <span className="font-mono text-xs">variant="success"</span>
                    — Green
                  </div>
                  <div>
                    <span className="font-mono text-xs">variant="warning"</span>
                    — Amber
                  </div>
                  <div>
                    <span className="font-mono text-xs">variant="error"</span>—
                    Red
                  </div>
                </div>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">Charts:</Text>
                <div className="bg-muted p-3 rounded-md text-sm space-y-1">
                  <div>BarChart, LineChart, AreaChart, PieChart, DonutChart</div>
                  <div>WaterfallChart, GaugeChart, TreemapChart</div>
                  <div className="text-xs text-muted-foreground mt-2">
                    All wrapped in &lt;ChartProvider&gt;
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Pattern Directives */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Pattern Directives</CardTitle>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2 text-success">
                  DO THIS:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`// Use theme tokens
<div className="p-theme-6 gap-theme-4 mb-theme-8">

// Wrap inputs in FormField
<FormField label="Email">
  <Input type="email" />
</FormField>

// Use Badge for status
<Badge variant="success">Completed</Badge>

// Controller pattern
export function useXxxController() {
  // Logic here
}

export function XxxPage() {
  const state = useXxxController()
  // Render only
}`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-destructive">
                  DON'T DO THIS:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`// Hardcode colors/spacing
<div className="p-6 text-info">

// Inline styles
<div style={{ padding: "24px" }}>

// Bare inputs
<label>Email</label>
<Input />

// Business logic in components
export function XxxPage() {
  const [data, setData] = useState([])
  useEffect(() => { fetch() }, [])
}

// Custom component versions
function MyButton() { ... }

// Mixing chart libraries
<BarChart ... />  // From recharts`}</code>
                </pre>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Anti-patterns */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Anti-Patterns to Avoid</CardTitle>

            <div className="space-y-theme-3">
              <div className="border-l-4 border-destructive pl-4">
                <Text className="font-semibold mb-1 text-sm">
                  Inline Styles
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Never use the style prop. Always use className with theme
                  tokens.
                </Text>
              </div>

              <div className="border-l-4 border-destructive pl-4">
                <Text className="font-semibold mb-1 text-sm">
                  Custom Component Versions
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Don't create MyButton or CustomCard. Use the library
                  components.
                </Text>
              </div>

              <div className="border-l-4 border-destructive pl-4">
                <Text className="font-semibold mb-1 text-sm">
                  Mixing Chart Libraries
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Only ECharts allowed. Never use Recharts or Chart.js.
                </Text>
              </div>

              <div className="border-l-4 border-destructive pl-4">
                <Text className="font-semibold mb-1 text-sm">
                  Business Logic in Components
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Move fetch, mutations, state to controller hooks. Keep
                  components pure.
                </Text>
              </div>

              <div className="border-l-4 border-destructive pl-4">
                <Text className="font-semibold mb-1 text-sm">
                  Skipping Layout Hierarchy
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Always use Scaffold → PageContainer → Section → SectionCard.
                  Never skip levels.
                </Text>
              </div>

              <div className="border-l-4 border-destructive pl-4">
                <Text className="font-semibold mb-1 text-sm">
                  Hardcoded Colors and Spacing
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Use theme tokens: p-theme-6, text-[var(--color-primary)],
                  etc.
                </Text>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Quick Start Template */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Quick Start Template</CardTitle>
            <Text className="text-sm text-muted-foreground mb-3">
              Use this as a starting point for any new page
            </Text>

            <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto">
              <code>{`"use client";

import React from "react";
import { useXxxController } from "@/hooks/useXxxController";
import { Scaffold } from "@/components/ui/bluecopa/ui/layout";
import { Sidebar } from "@/components/ui/bluecopa/ui/sidebar";
import { Topbar } from "@/components/ui/bluecopa/ui/topbar";
import { PageContainer, PageHeader, Section } from "@/components/ui/bluecopa/ui/layout";
import { SectionCard } from "@/components/ui/bluecopa/ui/card";
import { Button } from "@/components/ui/bluecopa/ui/button";

export default function XxxPage() {
  const { data, loading, error } = useXxxController();

  if (loading) return <XxxSkeleton />;

  return (
    <Scaffold sidebar={<Sidebar />} topbar={<Topbar />}>
      <PageContainer>
        <PageHeader
          title="Page Title"
          description="Optional description"
          action={<Button>Action</Button>}
        />

        <Section>
          <SectionCard>
            {/* Your content here */}
          </SectionCard>
        </Section>
      </PageContainer>
    </Scaffold>
  );
}

export { XxxPage };`}</code>
            </pre>
          </div>
        </SectionCard>
      </Section>

      {/* Theming Quick Ref */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Theming Quick Reference</CardTitle>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Spacing Tokens:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`p-theme-1 through p-theme-12
gap-theme-1 through gap-theme-12
mb-theme-1 through mb-theme-12`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Border Radius:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`rounded-theme-sm
rounded-theme-md
rounded-theme-lg
rounded-theme-xl`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">Shadows:</Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>shadow-theme</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Color Variables:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`--color-primary
--color-secondary
--color-success
--color-warning
--color-error
--color-background
--color-surface
--color-text`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">Usage:</Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`className="text-[var(--color-primary)]"`}</code>
                </pre>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Common Patterns */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Common Patterns</CardTitle>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Search + Filter + Table:
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Use ListingView component with SearchBox, filter inputs, and
                  DataTable
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  KPI Dashboard:
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Grid of KPICards, multiple charts in DashboardGrid wrapped in
                  ChartProvider
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Tabbed Detail View:
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Tabs with Overview, Details, History tabs showing different
                  content
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Multi-Step Approval:
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Stepper component with current step validation, Previous/Next
                  buttons
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Settings Page:
                </Text>
                <Text className="text-xs text-muted-foreground">
                  Vertical Tabs for sections, per-section forms with individual
                  save buttons
                </Text>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Page Recipes */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Page Recipes — Typography & Spacing</CardTitle>
            <Text className="text-sm text-muted-foreground">
              Every page MUST use these exact classes. Never use raw Tailwind sizes or hardcoded colors.
            </Text>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-theme-2 text-sm">Typography Hierarchy:</Text>
                <pre className="bg-muted p-theme-3 rounded-md text-xs overflow-x-auto">
                  <code>{`Page title          → text-h2 font-bold           (2.25rem / 700)
Page subtitle       → text-body text-muted-foreground (0.875rem / 400)
Section heading     → text-h4 font-semibold         (1.5rem / 600)
Card title          → text-body font-semibold        (0.875rem / 600)
Card description    → text-sm text-muted-foreground  (~0.835rem / 400)
Field label         → text-sm font-medium            (~0.835rem / 500)
KPI value           → text-2xl font-bold             (1.742rem / 700)
KPI label           → text-xs text-muted-foreground uppercase tracking-wide
Table header        → text-sm font-semibold          (~0.835rem / 600)
Table cell          → text-sm                        (~0.835rem / 400)
Badge               → text-xs font-medium            (0.702rem / 500)
Nav item            → text-sm font-medium            (~0.835rem / 500)
Helper text         → text-xs text-muted-foreground  (0.702rem / 400)`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-theme-2 text-sm">Spacing Rules:</Text>
                <pre className="bg-muted p-theme-3 rounded-md text-xs overflow-x-auto">
                  <code>{`Page container padding     → px-theme-8 py-theme-6 (via PageContainer)
Between page sections      → space-y-theme-6
Section header → content   → mb-theme-6 (via Section)
Between cards in section   → space-y-theme-4
Card internal padding      → px-theme-5 py-theme-5 (via Card)
Between form fields        → space-y-theme-4
Form field grid gap        → gap-theme-4
Button group gap           → gap-theme-3
Table cell padding         → px-theme-4 py-theme-3
Action bar above border    → pt-theme-5 mt-theme-5 border-t-theme
Small margins              → mb-theme-2
Tiny margins               → mt-theme-1`}</code>
                </pre>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Page Archetypes */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Page Archetypes</CardTitle>
            <Text className="text-sm text-muted-foreground">
              5 standard page structures. Pick the closest match for your use case.
            </Text>

            <div className="space-y-theme-3">
              <div className="border-l-4 border-primary pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">1. Dashboard / KPI Page</Text>
                <Text className="text-xs text-muted-foreground">
                  PageContainer → header (text-h2) → KPI grid (cols-4 gap-theme-4) → chart cards (cols-2 gap-theme-4) → data table Section
                </Text>
              </div>

              <div className="border-l-4 border-primary pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">2. Listing Page</Text>
                <Text className="text-xs text-muted-foreground">
                  PageContainer → header (text-h2 + Badge) → filter SectionCard (cols-3 gap-theme-4) → data table SectionCard → Pagination
                </Text>
              </div>

              <div className="border-l-4 border-primary pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">3. Detail Page</Text>
                <Text className="text-xs text-muted-foreground">
                  PageContainer(scrollable) → back link + header (text-h2) → Tabs → tab content in SectionCards (cols-2 gap-theme-6 for fields)
                </Text>
              </div>

              <div className="border-l-4 border-primary pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">4. Settings Page</Text>
                <Text className="text-xs text-muted-foreground">
                  PageContainer → header (text-h2) → grid-cols-4: sidebar nav (space-y-theme-2) + content col-span-3 (Section → SectionCard × N)
                </Text>
              </div>

              <div className="border-l-4 border-primary pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">5. Approval / Multi-Step Form</Text>
                <Text className="text-xs text-muted-foreground">
                  PageContainer → header (text-h2) + Stepper → step content in SectionCards (space-y-theme-4 for fields) → action bar (border-t-theme)
                </Text>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Common Mistakes */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Common Mistakes</CardTitle>

            <div className="space-y-theme-3">
              <div className="border-l-4 border-destructive pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">Wrong title sizes</Text>
                <Text className="text-xs text-muted-foreground">
                  text-3xl, text-2xl, text-h1 for page titles → Always use text-h2 font-bold
                </Text>
              </div>
              <div className="border-l-4 border-destructive pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">Raw Tailwind spacing</Text>
                <Text className="text-xs text-muted-foreground">
                  space-y-4, gap-6, p-3, mt-2 → Always use theme tokens: space-y-theme-4, gap-theme-6, p-theme-3, mt-theme-2
                </Text>
              </div>
              <div className="border-l-4 border-destructive pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">Hardcoded colors</Text>
                <Text className="text-xs text-muted-foreground">
                  rgb(), hex values in CSS or className → Always use semantic tokens: var(--foreground), text-muted-foreground, bg-primary
                </Text>
              </div>
              <div className="border-l-4 border-destructive pl-theme-4">
                <Text className="font-semibold mb-theme-1 text-sm">Inconsistent table spacing</Text>
                <Text className="text-xs text-muted-foreground">
                  py-3 px-4 on table cells → Always use px-theme-4 py-theme-3
                </Text>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Summary */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Summary Checklist</CardTitle>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>Use Scaffold → PageContainer → Section → SectionCard</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>Controller hook pattern for all state</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>ECharts wrapped in ChartProvider</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>DataTable {"<"}10K rows, AG Grid {">"}10K rows</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>Theme tokens for spacing, colors, shadows</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>FormField wrapper for all inputs</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>Badge for status (success, warning, error, info)</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>Dialog for modals, Sheet for panels</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>Skeleton for layout loading, Spinner for actions</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-success">✓</span>
                <span>Both default and named exports</span>
              </li>
            </ul>
          </div>
        </SectionCard>
      </Section>
    </PageContainer>
  );
}

export { AIInstructionsPage };
